import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RefreshCw, CheckCircle2, XCircle, AlertTriangle, Info,
  Calendar, Key, User, DollarSign, Globe, Shield,
  WifiOff, KeyRound, ArrowRight, Copy, Check,
  CreditCard, ExternalLink, HardDrive, PackagePlus, Users, Bot, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtFecha(s: string | null | undefined) {
  if (!s) return '—';
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
}

function fmtDateTime(d: Date) {
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

function fmtPrecio(n: number | null | undefined) {
  if (n == null) return 'Incluido';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) + ' / mes';
}

function fmtLimite(v: number | null | undefined) {
  if (v == null || v === -1) return 'Ilimitado';
  return String(v);
}

function periodoProgress(fechaActivacion: string | null, fechaVigencia: string | null): number {
  if (!fechaActivacion || !fechaVigencia) return 0;
  const inicio = new Date(fechaActivacion).getTime();
  const fin    = new Date(fechaVigencia).getTime();
  const ahora  = Date.now();
  if (fin <= inicio) return 100;
  return Math.min(100, Math.max(0, Math.round(((ahora - inicio) / (fin - inicio)) * 100)));
}

const fmtMXN = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n);

// ── Sub-componentes ───────────────────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={copy}
      className="ml-1.5 p-1 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
      title="Copiar"
    >
      {copied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
    </button>
  );
}

function Badge({ tipo }: { tipo: string | null }) {
  const isPaid = tipo === 'PG' || tipo === 'Pagado';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
      isPaid ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
    }`}>
      {isPaid ? 'Pagado' : 'Trial'}
    </span>
  );
}

function EstadoPago({ estado }: { estado: string | null }) {
  const map: Record<string, string> = {
    Pagado:    'bg-green-50 text-green-700',
    Pendiente: 'bg-amber-50 text-amber-700',
    Rechazado: 'bg-red-50 text-red-600',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[estado ?? ''] ?? 'bg-gray-50 text-gray-600'}`}>
      {estado ?? '—'}
    </span>
  );
}

function Row({
  icon: Icon, label, value, mono = false,
}: {
  icon: React.ElementType; label: string; value: string | null | undefined; mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-text-secondary">
        <div className="w-5 h-5 rounded bg-gray-50 flex items-center justify-center">
          <Icon size={11} className="text-gray-400" />
        </div>
        {label}
      </span>
      <span className={`font-medium text-text-primary ${mono ? 'font-mono text-xs' : ''}`}>
        {value ?? '—'}
      </span>
    </div>
  );
}

// ── Panel de activación inicial ───────────────────────────────────────────────

function ActivarPanel({ onSuccess }: { onSuccess: () => void }) {
  const [codigo, setCodigo] = useState('');

  const mut = useMutation({
    mutationFn: (code: string) =>
      api.post('/api/suscripcion/activar', { codigoActivacion: code }).then(r => r.data),
    onSuccess: () => {
      toast.success('Código registrado. Verificando con Neej...');
      onSuccess();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al registrar el código')),
  });

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
            <KeyRound size={24} className="text-primary-500" />
          </div>
          <h3 className="text-lg font-semibold text-text-primary">Activar licencia</h3>
          <p className="text-sm text-text-secondary mt-1">
            Ingresa el código de activación proporcionado
          </p>
        </div>
        <div className="space-y-3">
          <input
            value={codigo}
            onChange={e => setCodigo(e.target.value.toUpperCase())}
            placeholder="Ej. ACT-XXXX-XXXX-XXXXXX"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-primary-300 uppercase"
            onKeyDown={e => e.key === 'Enter' && codigo.trim() && mut.mutate(codigo.trim())}
          />
          <button
            onClick={() => mut.mutate(codigo.trim())}
            disabled={!codigo.trim() || mut.isPending}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {mut.isPending ? 'Registrando...' : 'Activar'}
            {!mut.isPending && <ArrowRight size={15} />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Tab: Licencia (Neej) ──────────────────────────────────────────────────────

function TabLicencia() {
  const qc = useQueryClient();
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['suscripcion-verificar'],
    queryFn:  () => api.get('/api/suscripcion/verificar').then(r => {
      setLastCheck(new Date());
      return r.data;
    }),
  });

  async function handleRefresh() {
    setIsRefreshing(true);
    await qc.invalidateQueries({ queryKey: ['suscripcion-verificar'] });
    setIsRefreshing(false);
  }

  const sinRegistro    = data?.error === 'SIN_REGISTRO_LOCAL';
  const local          = data?.local ?? null;
  const neej           = data?.neej ?? null;
  const pagos          = (data?.pagos ?? []) as any[];
  const dv             = data?.dominioVerificacion;
  const neejError      = data?.neejError ?? null;
  const diasRestantes  = neej?.diasRestantes ?? null;
  const esTrial        = local?.tipoLicencia === 'TR' || neej?.licencia?.tipo === 'TR';

  type BannerKind = 'sin-registro' | 'dominio-invalido' | 'sin-conexion' | 'activa' | 'expirada' | 'inactiva';

  function resolveBanner(): BannerKind {
    if (sinRegistro)                         return 'sin-registro';
    if (dv?.valido === false)                return 'dominio-invalido';
    if (neejError && !neej)                  return 'sin-conexion';
    if (neej?.status === 'ACTIVA')           return 'activa';
    if (neej?.status === 'EXPIRADA')         return 'expirada';
    if (neej?.status === 'INACTIVA')         return 'inactiva';
    return 'sin-conexion';
  }

  const banner = resolveBanner();

  const bannerCfg: Record<BannerKind, { bg: string; icon: React.ReactNode; title: string; sub?: string }> = {
    'sin-registro':    { bg: 'bg-gray-50 border-gray-200',       icon: <Key size={20} className="text-gray-400" />,          title: 'Sin licencia configurada',   sub: 'Ingresa un código de activación para comenzar.' },
    'dominio-invalido':{ bg: 'bg-red-50 border-red-200',         icon: <XCircle size={20} className="text-red-500" />,        title: 'DOMINIO NO AUTORIZADO',      sub: 'Este sistema está siendo ejecutado en un dominio no autorizado por la licencia.' },
    'sin-conexion':    { bg: 'bg-gray-50 border-gray-200',       icon: <WifiOff size={20} className="text-gray-400" />,       title: 'Sin conexión con Neej',      sub: 'Verificación en línea no disponible — mostrando datos locales.' },
    'activa':          { bg: 'bg-green-50 border-green-200',     icon: <CheckCircle2 size={20} className="text-green-500" />, title: 'Licencia activa',            sub: diasRestantes != null ? `${diasRestantes} día${diasRestantes !== 1 ? 's' : ''} restantes de vigencia` : 'Sin fecha de vencimiento' },
    'expirada':        { bg: 'bg-red-50 border-red-200',         icon: <XCircle size={20} className="text-red-500" />,        title: 'Licencia expirada',          sub: `Venció el ${fmtFecha(local?.fechaVigencia ?? neej?.licencia?.fechaVigencia)}` },
    'inactiva':        { bg: 'bg-amber-50 border-amber-200',     icon: <AlertTriangle size={20} className="text-amber-500" />, title: 'Licencia inactiva',         sub: neej?.razon ?? 'La licencia está desactivada.' },
  };

  const cfg = bannerCfg[banner];

  return (
    <div className="space-y-6">

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Estado verificado en tiempo real con el servidor Neej
        </p>
        {!sinRegistro && (
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={isRefreshing || isLoading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        )}
      </div>

      {/* Sin registro → panel de activación */}
      {sinRegistro && (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <ActivarPanel onSuccess={() => qc.invalidateQueries({ queryKey: ['suscripcion-verificar'] })} />
        </div>
      )}

      {!sinRegistro && isLoading && (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      )}

      {!sinRegistro && !isLoading && data && (<>

        {/* Alertas de negocio */}
        {esTrial && (
          <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200 text-sm text-blue-700">
            <Info size={16} className="flex-shrink-0" />
            Licencia de prueba — no apta para producción
          </div>
        )}
        {diasRestantes != null && diasRestantes <= 7 && diasRestantes > 0 && (
          <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-700">
            <AlertTriangle size={16} className="flex-shrink-0" />
            Tu licencia vence en {diasRestantes} día{diasRestantes !== 1 ? 's' : ''}
          </div>
        )}
        {diasRestantes != null && diasRestantes <= 0 && banner !== 'expirada' && (
          <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
            <XCircle size={16} className="flex-shrink-0" />
            Licencia vencida
          </div>
        )}
        {neejError && !neej && banner !== 'sin-conexion' && (
          <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-600">
            <WifiOff size={16} className="flex-shrink-0" />
            Verificación en línea no disponible — mostrando datos locales
          </div>
        )}

        {/* Banner de estado */}
        <div className={`flex items-start gap-3 p-4 rounded-xl border ${cfg.bg}`}>
          <div className="flex-shrink-0 mt-0.5">{cfg.icon}</div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-text-primary">{cfg.title}</p>
            {cfg.sub && <p className="text-sm text-text-secondary mt-0.5">{cfg.sub}</p>}
          </div>
          {local?.codigoActivacion && (
            <div className="flex items-center gap-1 flex-shrink-0 font-mono text-xs text-gray-500 bg-white/80 border border-gray-200 rounded-lg px-2.5 py-1.5">
              <Key size={11} />
              {local.codigoActivacion}
              <CopyButton text={local.codigoActivacion} />
            </div>
          )}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Card 1 — Licencia y Cliente */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center gap-2">
              <User size={13} className="text-gray-400" />
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                Licencia y Cliente
              </p>
            </div>
            <div className="p-5 space-y-4">
              <Row icon={User} label="Cliente" value={local?.nombreCliente} />
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-text-secondary">
                  <div className="w-5 h-5 rounded bg-gray-50 flex items-center justify-center">
                    <Key size={11} className="text-gray-400" />
                  </div>
                  Tipo
                </span>
                <Badge tipo={local?.tipoLicencia ?? neej?.licencia?.tipo} />
              </div>
              <Row icon={Globe} label="Dominio registrado" value={local?.dominio} mono />
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-text-secondary">
                  <div className="w-5 h-5 rounded bg-gray-50 flex items-center justify-center">
                    <Calendar size={11} className="text-gray-400" />
                  </div>
                  Período
                </span>
                <span className="text-text-primary font-medium">
                  {fmtFecha(local?.fechaActivacion)} → {local?.fechaVigencia ? fmtFecha(local.fechaVigencia) : 'Sin límite'}
                </span>
              </div>
              {local?.fechaActivacion && local?.fechaVigencia && (
                <div className="space-y-1">
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary-400 transition-all"
                      style={{ width: `${periodoProgress(local.fechaActivacion, local.fechaVigencia)}%` }}
                    />
                  </div>
                  <p className="text-xs text-text-secondary text-right">
                    {periodoProgress(local.fechaActivacion, local.fechaVigencia)}% transcurrido
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Card 2 — Plan */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center gap-2">
              <DollarSign size={13} className="text-gray-400" />
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                Plan
              </p>
            </div>
            <div className="p-5 space-y-4">
              <Row icon={Key} label="Nombre del plan" value={neej?.plan?.nombre ?? local?.nombrePlan} />
              <Row icon={DollarSign} label="Precio mensual" value={fmtPrecio(neej?.plan?.precio ?? local?.precio)} />

              {neej?.plan?.funcionalidades && Object.keys(neej.plan.funcionalidades).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                    Funcionalidades
                  </p>
                  <div className="rounded-lg border border-gray-100 overflow-hidden divide-y divide-gray-50">
                    {Object.entries(neej.plan.funcionalidades).map(([clave, valor]) => (
                      <div key={clave} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-text-secondary capitalize">
                          {clave.replace(/_/g, ' ')}
                        </span>
                        <span className={`font-medium ${valor == null || (valor as number) === -1 ? 'text-gray-400' : 'text-text-primary'}`}>
                          {fmtLimite(valor as number | null | undefined)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!neej?.plan?.funcionalidades && (
                <p className="text-sm text-text-secondary">
                  Funcionalidades no disponibles — Neej sin conexión
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Panel verificación de dominio */}
        <div className={`rounded-xl border overflow-hidden ${dv?.valido === false ? 'border-red-200' : 'border-gray-100'}`}>
          <div className={`px-5 py-3 border-b flex items-center gap-2 ${dv?.valido === false ? 'bg-red-50 border-red-100' : 'bg-gray-50/80 border-gray-100'}`}>
            <Shield size={13} className={dv?.valido === false ? 'text-red-500' : 'text-gray-400'} />
            <p className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
              Verificación de dominio
            </p>
            {dv?.valido === false && (
              <span className="ml-auto text-xs font-semibold text-red-600">No autorizado</span>
            )}
          </div>

          {dv?.valido === false && (
            <div className="px-5 py-3 bg-red-50 border-b border-red-100 text-sm text-red-700">
              Este sistema está siendo ejecutado en un dominio no autorizado por la licencia.
            </div>
          )}

          <div className="bg-white divide-y divide-gray-50">
            {[
              { label: 'Dominio actual',       value: dv?.dominioActual || '—', match: dv?.localMatch },
              { label: 'Dominio en licencia',  value: dv?.dominioLocal  || '—', match: dv?.localMatch },
              { label: 'Dominio en Neej',      value: dv?.dominioNeej   || (neejError ? 'Sin datos (Neej sin conexión)' : '—'), match: dv?.neejMatch },
            ].map(({ label, value, match }) => (
              <div key={label} className="flex items-center justify-between px-5 py-3 text-sm">
                <span className="text-text-secondary">{label}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-text-primary">{value}</span>
                  {match === true  && <CheckCircle2 size={14} className="text-green-500" />}
                  {match === false && <XCircle      size={14} className="text-red-500"   />}
                  {match === null  && <span className="text-xs text-gray-400">N/A</span>}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between px-5 py-3 text-sm font-semibold">
              <span className="text-text-primary">Estado general</span>
              {dv?.valido
                ? <span className="flex items-center gap-1.5 text-green-600"><CheckCircle2 size={14} /> Autorizado</span>
                : <span className="flex items-center gap-1.5 text-red-600"><XCircle size={14} /> No autorizado</span>
              }
            </div>
          </div>
        </div>

        {/* Historial de pagos */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center gap-2">
            <DollarSign size={13} className="text-gray-400" />
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
              Historial de pagos
            </p>
          </div>

          {pagos.length === 0 ? (
            <div className="py-10 text-center">
              <DollarSign size={32} className="mx-auto text-gray-200 mb-2" />
              <p className="text-sm text-text-secondary">
                {neejError ? 'Historial no disponible — Neej sin conexión' : 'Sin historial de pagos'}
              </p>
            </div>
          ) : (<>
            <div className="grid grid-cols-[1fr_110px_100px_1fr_120px_120px] gap-3 px-5 py-2.5 text-xs font-semibold text-text-secondary uppercase tracking-wide bg-gray-50/40 border-b border-gray-50">
              <span>Fecha pago</span>
              <span>Monto</span>
              <span>Método</span>
              <span>Estado</span>
              <span>Referencia</span>
              <span>Vence</span>
            </div>
            <div className="divide-y divide-gray-50">
              {[...pagos]
                .sort((a, b) => (b.fechaPago ?? '').localeCompare(a.fechaPago ?? ''))
                .map((p, i) => (
                  <div key={p.id ?? i} className="grid grid-cols-[1fr_110px_100px_1fr_120px_120px] gap-3 px-5 py-3 items-center text-sm">
                    <span className="text-text-primary">{fmtFecha(p.fechaPago)}</span>
                    <span className="font-semibold text-text-primary">
                      {p.monto != null
                        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: p.moneda ?? 'USD' }).format(p.monto)
                        : '—'}
                    </span>
                    <span className="text-text-secondary capitalize">{p.metodo ?? '—'}</span>
                    <EstadoPago estado={p.estado} />
                    <span className="text-text-secondary font-mono text-xs truncate">{p.referencia ?? '—'}</span>
                    <span className="text-text-secondary">{fmtFecha(p.fechaVencimiento)}</span>
                  </div>
                ))}
            </div>
          </>)}
        </div>

        {/* Pie */}
        <div className="flex items-center justify-between text-xs text-text-secondary pb-2">
          <span>
            {lastCheck
              ? `Última verificación: ${fmtDateTime(lastCheck)}`
              : 'Verificando...'}
          </span>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-1.5 hover:text-text-primary transition-colors disabled:opacity-50"
          >
            <RefreshCw size={12} className={isRefreshing ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>

      </>)}
    </div>
  );
}

// ── Tab: Planes (Stripe) ──────────────────────────────────────────────────────

const PLANES = [
  {
    id: 'solo' as const,
    nombre: 'Solo',
    descripcion: 'Para médicos independientes que digitalizan su consulta.',
    precios: { mes: 599, anio: 5967 },
    destacado: false,
    features: [
      '1 médico · 1 asistente',
      'Expediente electrónico',
      'Recetas y estudios digitales',
      'Teleconsulta',
      'Red médica',
      '5 GB almacenamiento',
      '60 consultas Ikal/mes',
    ],
  },
  {
    id: 'profesional' as const,
    nombre: 'Profesional',
    descripcion: 'Para equipos médicos que quieren crecer con más herramientas.',
    precios: { mes: 1299, anio: 13094 },
    features: [
      'Hasta 3 médicos · Asistentes ilimitados',
      'Finanzas y facturación',
      'Reportes avanzados',
      'Marketing (3 campañas)',
      'Red médica',
      '20 GB almacenamiento',
      '150 consultas Ikal/mes',
    ],
    destacado: true,
  },
  {
    id: 'clinica' as const,
    nombre: 'Clínica',
    descripcion: 'Para clínicas y grupos con necesidades avanzadas y múltiples sedes.',
    precios: { mes: 2999, anio: 30590 },
    destacado: false,
    features: [
      'Hasta 10 médicos · Ilimitados asistentes',
      'Consultorios ilimitados',
      'Finanzas y facturación',
      'Marketing ilimitado',
      'Reportes avanzados',
      '100 GB almacenamiento',
      'Consultas Ikal ilimitadas',
    ],
  },
] as const;

function TabPlanes() {
  const qc = useQueryClient();
  const [periodo, setPeriodo] = useState<'mes' | 'anio'>('mes');
  const [cambiandoId, setCambiandoId] = useState<string | null>(null);
  const periodoInit = useRef(false);

  const { data: stripeData } = useQuery({
    queryKey: ['stripe-status'],
    queryFn:  () => api.get('/api/suscripcion/stripe/status').then(r => r.data),
  });

  const checkoutMut = useMutation({
    mutationFn: ({ planId, per }: { planId: 'solo' | 'profesional' | 'clinica'; per: 'mes' | 'anio' }) =>
      api.post('/api/suscripcion/stripe/checkout', {
        planId,
        periodo: per,
        successUrl: `${window.location.origin}/configuracion/suscripcion?stripe=success`,
        cancelUrl:  `${window.location.origin}/configuracion/suscripcion`,
      }).then(r => r.data),
    onSuccess: (data) => { window.location.href = data.url; },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al iniciar el proceso de pago')),
  });

  const cambiarPlanMut = useMutation({
    mutationFn: ({ planId, per }: { planId: 'solo' | 'profesional' | 'clinica'; per: 'mes' | 'anio' }) =>
      api.patch('/api/suscripcion/stripe/plan', { planId, periodo: per }).then(r => r.data),
    onSuccess: () => {
      toast.success('Plan actualizado correctamente');
      qc.invalidateQueries({ queryKey: ['stripe-status'] });
      setCambiandoId(null);
    },
    onError: (err: any) => {
      toast.error(getErrorMessage(err, 'Error al cambiar el plan'));
      setCambiandoId(null);
    },
  });

  const portalMut = useMutation({
    mutationFn: () => api.get('/api/suscripcion/stripe/portal').then(r => r.data),
    onSuccess: (data) => { window.location.href = data.url; },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al abrir el portal de facturación')),
  });

  const stripeStatus    = stripeData?.subscriptionStatus as string | null;
  const hasSubscription = stripeStatus === 'active' || stripeStatus === 'trialing';
  const planActual      = stripeData?.planActual as { planId: string; periodo: string } | null;

  useEffect(() => {
    if (!periodoInit.current && planActual?.periodo) {
      setPeriodo(planActual.periodo as 'mes' | 'anio');
      periodoInit.current = true;
    }
  }, [planActual]);

  const statusMap: Record<string, { label: string; cls: string }> = {
    active:   { label: 'Activa',    cls: 'bg-green-50 text-green-700' },
    trialing: { label: 'Trial',     cls: 'bg-blue-50 text-blue-700'   },
    past_due: { label: 'Vencida',   cls: 'bg-red-50 text-red-600'     },
    canceled: { label: 'Cancelada', cls: 'bg-gray-50 text-gray-600'   },
  };
  const statusCfg = stripeStatus
    ? (statusMap[stripeStatus] ?? { label: stripeStatus, cls: 'bg-gray-50 text-gray-600' })
    : null;

  return (
    <div className="space-y-6">

      {/* Barra de estado + portal */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard size={15} className="text-text-secondary" />
          <span className="text-sm text-text-secondary">Suscripción actual</span>
          {statusCfg ? (
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${statusCfg.cls}`}>
              {statusCfg.label}
            </span>
          ) : (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-50 text-gray-500">
              Sin suscripción
            </span>
          )}
        </div>
        {hasSubscription && (
          <button
            onClick={() => portalMut.mutate()}
            disabled={portalMut.isPending}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <ExternalLink size={13} />
            {portalMut.isPending ? 'Abriendo...' : 'Facturación y cancelación'}
          </button>
        )}
      </div>

      {/* Cards de planes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center py-2">
        {PLANES.map(plan => {
          const precio    = plan.precios[periodo];
          const precioMes = periodo === 'anio'
            ? Math.round(plan.precios.anio / 12)
            : plan.precios.mes;
          const displayPrice = periodo === 'anio' ? precioMes : precio;

          const esPlanActual = hasSubscription
            && planActual?.planId === plan.id
            && planActual?.periodo === periodo;

          const esResaltado  = hasSubscription ? esPlanActual : plan.destacado;
          const isCambiando  = cambiandoId === `${plan.id}_${periodo}`;

          return (
            <div
              key={plan.id}
              className={`relative flex flex-col rounded-3xl overflow-hidden transition-all ${
                esResaltado
                  ? 'bg-gradient-to-b from-primary-600 to-primary-400 shadow-2xl shadow-primary-200/60 py-10 px-7 z-10'
                  : 'bg-white border border-gray-100 shadow-md py-7 px-6'
              }`}
            >
              {/* Nombre + descripción */}
              <div className="text-center mb-5">
                <p className={`text-xs font-bold uppercase tracking-widest mb-1 ${
                  esResaltado ? 'text-white' : 'text-text-secondary'
                }`}>
                  {plan.nombre}
                </p>
                <p className={`text-xs leading-relaxed ${esResaltado ? 'text-white/70' : 'text-text-secondary'}`}>
                  {plan.descripcion}
                </p>
              </div>

              {/* Precio */}
              <div className="text-center mb-4">
                <span className={`text-5xl font-extrabold ${esResaltado ? 'text-white' : 'text-primary-500'}`}>
                  ${displayPrice.toLocaleString('es-MX')}
                </span>
                <p className={`text-xs mt-1 ${esResaltado ? 'text-white/60' : 'text-text-secondary'}`}>
                  MXN/mes{periodo === 'anio' ? ` · ${fmtMXN(precio)}/año` : ''}
                </p>
              </div>

              {/* Toggle período */}
              <div className="flex justify-center mb-6">
                <div className={`inline-flex items-center gap-0.5 p-0.5 rounded-full text-xs font-medium ${
                  esResaltado ? 'bg-white/20' : 'bg-gray-100'
                }`}>
                  <button
                    onClick={() => setPeriodo('mes')}
                    className={`px-3 py-1 rounded-full transition-colors ${
                      periodo === 'mes'
                        ? (esResaltado ? 'bg-white text-primary-600' : 'bg-white text-text-primary shadow-sm')
                        : (esResaltado ? 'text-white/70 hover:text-white' : 'text-text-secondary hover:text-text-primary')
                    }`}
                  >
                    Mensual
                  </button>
                  <button
                    onClick={() => setPeriodo('anio')}
                    className={`px-3 py-1 rounded-full transition-colors ${
                      periodo === 'anio'
                        ? (esResaltado ? 'bg-white text-primary-600' : 'bg-white text-text-primary shadow-sm')
                        : (esResaltado ? 'text-white/70 hover:text-white' : 'text-text-secondary hover:text-text-primary')
                    }`}
                  >
                    Anual
                  </button>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 flex-1 mb-7">
                {plan.features.map(f => (
                  <li key={f} className={`flex items-center gap-2.5 text-sm ${
                    esResaltado ? 'text-white/90' : 'text-text-secondary'
                  }`}>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                      esResaltado ? 'bg-white/20' : 'bg-primary-50'
                    }`}>
                      <CheckCircle2 size={12} className={esResaltado ? 'text-white' : 'text-primary-500'} />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {esPlanActual ? (
                <div className="w-full py-3 rounded-full text-sm font-bold text-center bg-white/30 text-white cursor-default">
                  PLAN ACTUAL
                </div>
              ) : hasSubscription ? (
                <button
                  onClick={() => {
                    setCambiandoId(`${plan.id}_${periodo}`);
                    cambiarPlanMut.mutate({ planId: plan.id, per: periodo });
                  }}
                  disabled={cambiarPlanMut.isPending}
                  className={`w-full py-3 rounded-full text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50 ${
                    esResaltado
                      ? 'bg-white text-primary-600 hover:bg-primary-50'
                      : 'border-2 border-primary-500 text-primary-500 hover:bg-primary-50'
                  }`}
                >
                  {isCambiando ? 'Cambiando...' : 'Cambiar'}
                </button>
              ) : (
                <button
                  onClick={() => checkoutMut.mutate({ planId: plan.id, per: periodo })}
                  disabled={checkoutMut.isPending}
                  className={`w-full py-3 rounded-full text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-50 ${
                    esResaltado
                      ? 'bg-white text-primary-600 hover:bg-primary-50'
                      : 'border-2 border-primary-500 text-primary-500 hover:bg-primary-50'
                  }`}
                >
                  {checkoutMut.isPending ? 'Redirigiendo...' : 'Contratar'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {hasSubscription && (
        <p className="text-xs text-text-secondary text-center">
          Los cambios de plan aplican prorrateo automático. Para cancelar tu suscripción usa "Facturación y cancelación".
        </p>
      )}

    </div>
  );
}

// ── Tab: Add-ons ─────────────────────────────────────────────────────────────

function fmtBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function PackStepper({
  packs,
  min = 0,
  max = 50,
  disabled,
  isPending,
  onChange,
  onSave,
}: {
  packs: number;
  min?: number;
  max?: number;
  disabled: boolean;
  isPending: boolean;
  onChange: (n: number) => void;
  onSave: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0 border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => onChange(Math.max(min, packs - 1))}
          disabled={disabled || packs <= min || isPending}
          className="w-12 h-8 flex items-center justify-center text-text-secondary hover:bg-gray-50 disabled:opacity-30 transition-colors text-base font-medium"
        >
          −
        </button>
        <span className="w-9 h-8 flex items-center justify-center text-sm font-semibold text-text-primary border-x border-gray-200 tabular-nums">
          {packs}
        </span>
        <button
          onClick={() => onChange(Math.min(max, packs + 1))}
          disabled={disabled || packs >= max || isPending}
          className="w-12 h-8 flex items-center justify-center text-text-secondary hover:bg-gray-50 disabled:opacity-30 transition-colors text-base font-medium"
        >
          +
        </button>
      </div>
      <button
        onClick={onSave}
        disabled={disabled || isPending}
        className="flex-1 h-8 rounded-lg bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600 transition-colors disabled:opacity-40 px-3 whitespace-nowrap"
      >
        {isPending ? '...' : 'Adquirir'}
      </button>
    </div>
  );
}

type AddonCardProps = {
  icon: React.ElementType;
  title: string;
  priceLabel: string;
  usageLabel: string;
  usageDetail: string;
  pct: number;
  atLimit: boolean;
  isUnlimited: boolean;
  packs: number;
  hasSubscription: boolean;
  isPending: boolean;
  onSave: (n: number) => void;
  max?: number;
};

function AddonCard({
  icon: Icon, title, priceLabel,
  usageLabel, usageDetail,
  pct, atLimit, isUnlimited,
  packs, hasSubscription, isPending, onSave, max = 20,
}: AddonCardProps) {
  const [localPacks, setLocalPacks] = useState(packs);
  useEffect(() => { setLocalPacks(packs); }, [packs]);

  const barColor = atLimit ? 'bg-red-500' : pct >= 70 ? 'bg-amber-400' : 'bg-primary-400';

  return (
    <div className={`bg-white rounded-2xl border overflow-hidden flex flex-col ${
      atLimit ? 'border-red-200' : 'border-gray-100'
    }`}>
      {/* Header */}
      <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
            atLimit ? 'bg-red-50' : 'bg-primary-50'
          }`}>
            <Icon size={15} className={atLimit ? 'text-red-500' : 'text-primary-500'} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary leading-tight">{title}</p>
            <p className="text-xs text-text-secondary mt-0.5">{priceLabel}</p>
          </div>
        </div>
        {atLimit && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-50 text-red-600 text-xs font-semibold flex-shrink-0">
            <AlertTriangle size={10} />
            Lleno
          </span>
        )}
      </div>

      {/* Usage */}
      <div className="px-4 pb-4 flex-1 flex flex-col gap-3">
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-xl font-bold text-text-primary tabular-nums">{usageLabel}</span>
            {!isUnlimited && (
              <span className="text-xs text-text-secondary">{pct}%</span>
            )}
          </div>
          {!isUnlimited && (
            <div className="w-full h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
          )}
          <p className="text-xs text-text-secondary mt-1.5 leading-relaxed">{usageDetail}</p>
        </div>

        {/* Stepper */}
        <div className="mt-auto pt-1">
          {!hasSubscription ? (
            <p className="text-xs text-text-secondary flex items-center gap-1">
              <Info size={11} className="flex-shrink-0" />
              Requiere plan Stripe activo
            </p>
          ) : isUnlimited ? (
            <p className="text-xs text-green-600 font-medium">Ilimitado en tu plan</p>
          ) : (
            <PackStepper
              packs={localPacks}
              min={0}
              max={max}
              disabled={!hasSubscription}
              isPending={isPending}
              onChange={setLocalPacks}
              onSave={() => onSave(localPacks)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function TabAddons() {
  const qc = useQueryClient();

  const { data: addonsData, isLoading } = useQuery({
    queryKey: ['addons-info'],
    queryFn:  () => api.get('/api/suscripcion/addons').then(r => r.data),
  });

  const { data: stripeData } = useQuery({
    queryKey: ['stripe-status'],
    queryFn:  () => api.get('/api/suscripcion/stripe/status').then(r => r.data),
  });

  const storageMut = useMutation({
    mutationFn: (cantidad: number) =>
      api.post('/api/suscripcion/storage/addon', { cantidad }).then(r => r.data),
    onSuccess: () => { toast.success('Almacenamiento actualizado'); qc.invalidateQueries({ queryKey: ['addons-info'] }); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar almacenamiento')),
  });

  const medicoMut = useMutation({
    mutationFn: (cantidad: number) =>
      api.post('/api/suscripcion/medico-adicional/addon', { cantidad }).then(r => r.data),
    onSuccess: () => { toast.success('Médicos adicionales actualizados'); qc.invalidateQueries({ queryKey: ['addons-info'] }); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar médicos adicionales')),
  });

  const ikalMut = useMutation({
    mutationFn: (cantidad: number) =>
      api.post('/api/suscripcion/ikal-ai/addon', { cantidad }).then(r => r.data),
    onSuccess: () => { toast.success('Ikal AI actualizado'); qc.invalidateQueries({ queryKey: ['addons-info'] }); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar Ikal AI')),
  });

  const consultorioMut = useMutation({
    mutationFn: (cantidad: number) =>
      api.post('/api/suscripcion/consultorio-adicional/addon', { cantidad }).then(r => r.data),
    onSuccess: () => { toast.success('Consultorios actualizados'); qc.invalidateQueries({ queryKey: ['addons-info'] }); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar consultorios')),
  });

  // Neej es la fuente de verdad para límites base del plan
  // (ya cacheado si TabLicencia cargó primero; sino lo carga aquí)
  const { data: verificarData } = useQuery({
    queryKey: ['suscripcion-verificar'],
    queryFn:  () => api.get('/api/suscripcion/verificar').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const stripeStatus    = stripeData?.subscriptionStatus as string | null;
  const hasSubscription = stripeStatus === 'active' || stripeStatus === 'trialing';

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3">
        {[1,2,3,4].map(i => (
          <div key={i} className="h-48 rounded-2xl bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  // Funcionalidades del plan desde Neej (fuente de verdad)
  // con fallback al caché local de la tabla suscripcion
  const nf: Record<string, any> = verificarData?.neej?.plan?.funcionalidades ?? {};

  // ── Storage ────────────────────────────────────────────────────────────────
  const gbBase       = nf.almacenamiento !== undefined ? Number(nf.almacenamiento) : (addonsData?.storage?.gbBase ?? 5);
  const gbExtraPacks = addonsData?.storage?.gbExtraPacks ?? 0;
  const bytesUsados  = addonsData?.storage?.bytesUsados  ?? 0;
  const gbTotal      = gbBase + gbExtraPacks * 10;
  const bytesTotal   = gbTotal * 1024 * 1024 * 1024;
  const storagePct   = bytesTotal > 0 ? Math.min(100, Math.round((bytesUsados / bytesTotal) * 100)) : 0;

  // ── Médicos ────────────────────────────────────────────────────────────────
  const medBase  = nf.medicos       !== undefined ? Number(nf.medicos)  : (addonsData?.medicos?.base ?? 1);
  const medAdd   = addonsData?.medicos?.adicionales  ?? 0;
  const medCount = addonsData?.medicos?.totalActuales ?? 0;
  const medTotal = medBase === -1 ? -1 : medBase + medAdd;
  const medPct   = medBase === -1 ? 0 : Math.min(100, Math.round((medCount / Math.max(1, medTotal)) * 100));

  // ── Ikal ───────────────────────────────────────────────────────────────────
  const ikalBase  = nf.ikal         !== undefined ? Number(nf.ikal)     : (addonsData?.ikal?.consultasBase ?? 50);
  const ikalPacks = addonsData?.ikal?.extraPacks  ?? 0;
  const ikalUsed  = addonsData?.ikal?.consultasMes ?? 0;
  const ikalTotal = ikalBase === -1 ? -1 : ikalBase + ikalPacks * 100;
  const ikalPct   = ikalBase === -1 ? 0 : Math.min(100, Math.round((ikalUsed / Math.max(1, ikalTotal)) * 100));

  // ── Consultorios ───────────────────────────────────────────────────────────
  const conBase  = nf.consultorios  !== undefined ? Number(nf.consultorios) : (addonsData?.consultorios?.base ?? 1);
  const conAdd   = addonsData?.consultorios?.adicionales  ?? 0;
  const conCount = addonsData?.consultorios?.totalActuales ?? 0;
  const conTotal = conBase === -1 ? -1 : conBase + conAdd;
  const conPct   = conBase === -1 ? 0 : Math.min(100, Math.round((conCount / Math.max(1, conTotal)) * 100));

  return (
    <div className="space-y-4">

      {!hasSubscription && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-700">
          <Info size={13} className="flex-shrink-0" />
          Los add-ons requieren una suscripción Stripe activa. Actívala en la pestaña <strong className="ml-0.5">Planes</strong>.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

        <AddonCard
          icon={HardDrive}
          title="Almacenamiento"
          priceLabel="$89 MXN/mes · 10 GB por pack"
          usageLabel={fmtBytes(bytesUsados)}
          usageDetail={`${fmtBytes(Math.max(0, bytesTotal - bytesUsados))} disponibles · ${gbBase} GB base${gbExtraPacks > 0 ? ` + ${gbExtraPacks * 10} GB extra` : ''}`}
          pct={storagePct}
          atLimit={storagePct >= 100}
          isUnlimited={false}
          packs={gbExtraPacks}
          hasSubscription={hasSubscription}
          isPending={storageMut.isPending}
          onSave={storageMut.mutate}
          max={50}
        />

        <AddonCard
          icon={Users}
          title="Médicos"
          priceLabel="$248 MXN/mes · 1 médico por pack"
          usageLabel={medBase === -1 ? `${medCount}` : `${medCount} / ${medTotal}`}
          usageDetail={
            medBase === -1
              ? 'Médicos ilimitados en tu plan'
              : `${medBase} del plan${medAdd > 0 ? ` + ${medAdd} extra` : ''} · ${Math.max(0, medTotal - medCount)} disponibles`
          }
          pct={medPct}
          atLimit={medBase !== -1 && medCount >= medTotal}
          isUnlimited={medBase === -1}
          packs={medAdd}
          hasSubscription={hasSubscription}
          isPending={medicoMut.isPending}
          onSave={medicoMut.mutate}
        />

        <AddonCard
          icon={Bot}
          title="Ikal AI"
          priceLabel="$199 MXN/mes · 100 consultas por pack"
          usageLabel={ikalBase === -1 ? `${ikalUsed}` : `${ikalUsed} / ${ikalTotal}`}
          usageDetail={
            ikalBase === -1
              ? 'Consultas ilimitadas en tu plan'
              : `Este mes (${addonsData?.ikal?.mesActual ?? ''}) · ${Math.max(0, ikalTotal - ikalUsed)} restantes · ${ikalBase} del plan${ikalPacks > 0 ? ` + ${ikalPacks * 100} extra` : ''}`
          }
          pct={ikalPct}
          atLimit={ikalBase !== -1 && ikalUsed >= ikalTotal}
          isUnlimited={ikalBase === -1}
          packs={ikalPacks}
          hasSubscription={hasSubscription}
          isPending={ikalMut.isPending}
          onSave={ikalMut.mutate}
        />

        <AddonCard
          icon={Building2}
          title="Consultorios"
          priceLabel="$199 MXN/mes · 1 consultorio por pack"
          usageLabel={conBase === -1 ? `${conCount}` : `${conCount} / ${conTotal}`}
          usageDetail={
            conBase === -1
              ? 'Consultorios ilimitados en tu plan'
              : `${conBase} del plan${conAdd > 0 ? ` + ${conAdd} extra` : ''} · ${Math.max(0, conTotal - conCount)} disponibles`
          }
          pct={conPct}
          atLimit={conBase !== -1 && conCount >= conTotal}
          isUnlimited={conBase === -1}
          packs={conAdd}
          hasSubscription={hasSubscription}
          isPending={consultorioMut.isPending}
          onSave={consultorioMut.mutate}
        />

      </div>

      <p className="text-xs text-center text-text-secondary pb-1">
        Los cambios aplican prorrateo automático desde hoy.
      </p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════

type Tab = 'licencia' | 'planes' | 'addons';

const TABS: { id: Tab; label: string }[] = [
  { id: 'licencia', label: 'Licencia' },
  { id: 'planes',   label: 'Planes'   },
  { id: 'addons',   label: 'Add-ons'  },
];

export function SuscripcionPage() {
  const [tab, setTab] = useState<Tab>('licencia');

  return (
    <div className="space-y-6">

      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-text-primary">Suscripción y Licencia</h2>
      </div>

      {/* Pestañas */}
      <div className="border-b border-gray-100">
        <nav className="flex gap-1">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                tab === t.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Contenido */}
      {tab === 'licencia' && <TabLicencia />}
      {tab === 'planes'   && <TabPlanes />}
      {tab === 'addons'   && <TabAddons />}

    </div>
  );
}
